import { Dispatch } from "PFApp/search/search_context";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringify";
import { ActivityResponseData } from "PFCore/services/activities/activities";
import { SEARCH_ACTIONS, SEARCH_ASYNC_ACTIONS } from "PFReducers/search_reducer/search_reducer";
import { Collection, Filters, OrderMeta, Template, Value } from "PFTypes";
import { useEffect, useMemo } from "react";

import { getAPIFilters } from "../../use_filtered_collection";
import { useDebouncedMergeState } from "../helpers/use_debounced_merge_state";

type ActivitiesSyncArgs = {
  dispatch: Dispatch;
  term: string;
  trimmedTerm: string;
  triggerCounter: number;
  tab: string;
  availableTemplates: Template[];
  activitiesRequestFilters: { [templateKey: string]: Filters<Value> };
  activitiesRequestOrder: { [templateKey: string]: Partial<OrderMeta> };
  activitiesPagination: Record<string, number>;
  allActivities: Record<string, Collection<ActivityResponseData[]>>;
};

export const useActivitiesSync = ({
  dispatch,
  term,
  trimmedTerm,
  triggerCounter,
  tab,
  availableTemplates,
  activitiesRequestFilters,
  activitiesRequestOrder,
  activitiesPagination,
  allActivities
}: ActivitiesSyncArgs) => {
  const stringify = useDeterministicStringify();
  const debouncedGoToPage1Dispatch = useDebouncedMergeState(dispatch);

  const activitiesRequestAPIFilters = useMemo(
    () =>
      availableTemplates.reduce(
        (acc, template) => {
          acc[template.key] = getAPIFilters(activitiesRequestFilters[template.key]) || {};
          return acc;
        },
        {} as Record<string, Filters<Value>>
      ),
    [availableTemplates, activitiesRequestFilters]
  );

  const activitiesParamsHash = stringify([activitiesRequestAPIFilters, activitiesRequestOrder]);

  const fetchAllActivitiesDebounced = useDebounce(
    () =>
      dispatch({
        type: SEARCH_ASYNC_ACTIONS.SEARCH_FETCH_ALL_ACTIVITIES,
        payload: {
          term,
          availableTemplates,
          activitiesRequestOrder,
          activitiesRequestFilters: activitiesRequestAPIFilters
        }
      }),
    500
  );

  // update activities when page is changed
  useEffect(() => {
    const page = activitiesPagination && activitiesPagination[tab];
    if (page > 1) {
      dispatch({
        type: SEARCH_ACTIONS.SEARCH_MORE_ACTIVITIES,
        payload: {
          allActivities,
          tab,
          activitiesPagination,
          availableTemplates,
          term,
          activitiesRequestFilters: activitiesRequestAPIFilters,
          activitiesRequestOrder
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activitiesPagination]);

  // update activities when filters are changed
  useEffect(() => {
    dispatch({
      type: SEARCH_ACTIONS.SEARCH_MORE_ACTIVITIES,
      payload: {
        allActivities,
        tab,
        availableTemplates,
        term,
        activitiesRequestFilters: activitiesRequestAPIFilters,
        activitiesRequestOrder
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activitiesParamsHash]);

  useEffect(() => {
    debouncedGoToPage1Dispatch({ activitiesPagination: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activitiesParamsHash]);

  useEffect(() => {
    dispatch({ type: SEARCH_ACTIONS.SEARCH_ABORT_ACTIVITIES });
    fetchAllActivitiesDebounced();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedTerm, triggerCounter]);
};
